import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { Edit, MoreHorizontal, Package, Plus, Printer, QrCode, Trash2, Wrench } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AssetInstance, AssetTemplate } from '@/types/assetTemplate';
import { canAccessReservations, canEditProducts, readStoredUser } from '../lib/roles';
import { toUnitLabelItems } from '../utils/fetchInstancesForLabels';
import { printUnitLabelPdf } from '../utils/unitLabelPdfGenerator';
import AssetInstanceForm from './AssetInstanceForm';
import BulkInstanceCreator from './BulkInstanceCreator';
import CreateReservationModal from './CreateReservationModal';
import PrintLabelsDialog from './PrintLabelsDialog';
import { Button } from './ui/button';
import { createBadgeColumn, createDateColumn } from './ui/column-helpers';
import { DataTable } from './ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

interface AssetInstancesTabProps {
  templateId: string;
  template: AssetTemplate;
}

const AssetInstancesTab: React.FC<AssetInstancesTabProps> = ({ templateId, template }) => {
  const [instances, setInstances] = useState<AssetInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AssetInstance | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    rented: 0,
    maintenance: 0,
    retired: 0,
    lost: 0,
    damaged: 0,
    next_unit_sequence: 1,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDialogMode, setPrintDialogMode] = useState<'selected' | 'all' | 'range'>('selected');
  const [reserveIds, setReserveIds] = useState<string[]>([]);
  const [reserveOpen, setReserveOpen] = useState(false);
  const navigate = useNavigate();

  const selectedInstances = useMemo(
    () => instances.filter((i) => rowSelection[i.id]),
    [instances, rowSelection],
  );
  const selectedCount = selectedInstances.length;

  const printSingleLabel = (instance: AssetInstance) => {
    try {
      printUnitLabelPdf(toUnitLabelItems([instance], template.name, template.template_code));
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to print label');
    }
  };

  const openPrintDialog = (mode: 'selected' | 'all' | 'range' = 'selected') => {
    setPrintDialogMode(mode);
    setPrintDialogOpen(true);
  };

  const loadInstances = useCallback(async (currentPage = page, currentLimit = limit) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/asset-instances?template_id=${templateId}&page=${currentPage}&limit=${currentLimit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load instances');
      }

      const data = await response.json();
      setInstances(data.success ? data.data : data);
      if (data.pagination) {
        setPagination({
          total: data.pagination.total,
          page: data.pagination.page,
          limit: data.pagination.limit,
          pages: data.pagination.totalPages,
        });
      }
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading instances:', err);
    } finally {
      setLoading(false);
    }
  }, [templateId, page, limit]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const startCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const startEdit = (instance: AssetInstance) => {
    setEditing(instance);
    setDialogOpen(true);
  };

  const startBulkCreate = () => {
    setBulkDialogOpen(true);
  };

  const handleFormSuccess = () => {
    setDialogOpen(false);
    setEditing(null);
    loadInstances();
  };

  const handleBulkSuccess = () => {
    setBulkDialogOpen(false);
    loadInstances();
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
    loadInstances(newPage, newLimit);
  };

  const handleDelete = async (instance: AssetInstance) => {
    if (!window.confirm(`Are you sure you want to delete instance "${instance.instance_number}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-instances/${instance.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete instance');
      }

      loadInstances();
    } catch (err) {
      console.error('Error deleting instance:', err);
      window.alert('Failed to delete instance. Please try again.');
    }
  };

  const getConditionBadge = (condition: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
      new: 'default',
      pre_loved: 'secondary',
      good: 'secondary',
      damaged: 'destructive',
      needs_repair: 'warning',
      missing: 'destructive',
      retired: 'secondary',
    };
    return variants[condition] || 'secondary';
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
      available: 'default',
      reserved: 'warning',
      booked: 'secondary',
      rented: 'secondary',
      maintenance: 'warning',
      retired: 'secondary',
      lost: 'destructive',
      damaged: 'destructive',
    };
    return variants[status] || 'secondary';
  };

  const formatCondition = (condition: string) => {
    return condition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const columns: ColumnDef<AssetInstance>[] = [
    {
      accessorKey: 'instance_number',
      header: 'Unit #',
      cell: ({ row }) => {
        const instance = row.original as AssetInstance;
        const primaryImage = instance.template?.images?.find((img: any) => img.variant === 'web');
        const imageUrl = primaryImage?.url || instance.template?.images?.[0]?.url;

        return (
          <button
            onClick={() => navigate(`/units/${instance.id}`)}
            className="flex items-center gap-3 text-left hover:text-primary"
            title="View unit details"
          >
            {imageUrl ? (
              <div className="flex-shrink-0 w-16 h-16 bg-muted rounded overflow-hidden">
                <img
                  src={imageUrl}
                  alt={instance.template?.name || 'Product'}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="flex-shrink-0 w-16 h-16 bg-muted rounded flex items-center justify-center">
                <Package className="h-6 w-6 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-mono font-medium underline-offset-2 hover:underline">
                {row.getValue('instance_number')}
              </span>
              {instance.template?.name && (
                <span className="text-xs text-muted-foreground">
                  {instance.template.name}
                </span>
              )}
            </div>
          </button>
        );
      },
    },
    {
      accessorKey: 'serial_number',
      header: 'Serial Number',
      cell: ({ row }) => {
        const serialNumber = row.getValue('serial_number') as string;
        return serialNumber ? (
          <span className="font-mono text-sm">{serialNumber}</span>
        ) : (
          <span className="text-gray-400 italic">No serial</span>
        );
      },
    },
    createBadgeColumn<AssetInstance>('current_condition', 'Condition', {
      getBadgeVariant: (value) => getConditionBadge(value),
      formatValue: formatCondition,
    }),
    createBadgeColumn<AssetInstance>('current_status', 'Status', {
      getBadgeVariant: (value) => getStatusBadge(value),
      formatValue: formatStatus,
    }),
    {
      accessorKey: 'current_location',
      header: 'Location',
      cell: ({ row }) => {
        const location = row.getValue('current_location') as AssetInstance['current_location'];
        return location ? (
          <div>
            <p className="font-medium">{location.address_line1}</p>
            {location.city && location.region && (
              <p className="text-sm text-gray-500">{location.city}, {location.region}</p>
            )}
          </div>
        ) : (
          <span className="text-gray-400 italic">No location</span>
        );
      },
    },
    {
      accessorKey: 'acquisition_cost',
      header: 'Cost',
      cell: ({ row }) => {
        const cost = row.getValue('acquisition_cost') as number;
        return cost ? (
          <span className="font-medium">£{cost.toLocaleString()}</span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
    },
    createDateColumn<AssetInstance>('created_at', 'Created'),
    {
      accessorKey: 'created_by_user',
      header: 'Created By',
      cell: ({ row }) => {
        const user = row.getValue('created_by_user') as AssetInstance['created_by_user'];
        return user ? (
          <span>{user.first_name} {user.last_name}</span>
        ) : (
          <span className="text-gray-400">—</span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const instance = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/units/${instance.id}`)}>
                View Details
              </DropdownMenuItem>
              {canAccessReservations(readStoredUser()) && instance.current_status === 'available' && (
                <DropdownMenuItem onClick={() => { setReserveIds([instance.id]); setReserveOpen(true); }}>
                  Reserve unit
                </DropdownMenuItem>
              )}
              {canEditProducts(readStoredUser()) && (
              <DropdownMenuItem onClick={() => startEdit(instance)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Unit
              </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => printSingleLabel(instance)}>
                <Printer className="h-4 w-4 mr-2" />
                Print Label
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.alert('QR Code generation coming soon!')}>
                <QrCode className="h-4 w-4 mr-2" />
                View QR Code
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.alert('Maintenance scheduling coming soon!')}>
                <Wrench className="h-4 w-4 mr-2" />
                Schedule Maintenance
              </DropdownMenuItem>
              {canEditProducts(readStoredUser()) && (
              <DropdownMenuItem
                onClick={() => handleDelete(instance)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Unit
              </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Units</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-green-600">{stats.available}</div>
          <div className="text-sm text-gray-600">Available</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-blue-600">{stats.rented}</div>
          <div className="text-sm text-gray-600">Rented</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-orange-600">{stats.maintenance}</div>
          <div className="text-sm text-gray-600">Maintenance</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-red-600">{stats.lost}</div>
          <div className="text-sm text-gray-600">Lost</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-2xl font-bold text-red-600">{stats.damaged}</div>
          <div className="text-sm text-gray-600">Damaged</div>
        </div>
      </div>

      {/* Units Table */}
      <DataTable
        columns={columns}
        data={instances}
        loading={loading}
        serverSide={true}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row) => row.id}
        toolbarContentRight={(
          <>
            {canAccessReservations(readStoredUser()) && (
              <Button
                variant="outline"
                disabled={!selectedInstances.some((i) => i.current_status === 'available')}
                onClick={() => {
                  const ids = selectedInstances.filter((i) => i.current_status === 'available').map((i) => i.id);
                  setReserveIds(ids);
                  setReserveOpen(true);
                }}
              >
                Reserve selected
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => openPrintDialog(selectedCount > 0 ? 'selected' : 'all')}
              disabled={stats.total === 0}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Labels{selectedCount > 0 ? ` (${selectedCount})` : ''}
            </Button>
            {canEditProducts(readStoredUser()) && (
              <>
            <Button onClick={startCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Unit
            </Button>
            <Button variant="outline" onClick={startBulkCreate}>
              <Package className="h-4 w-4 mr-2" />
              Bulk Create
            </Button>
              </>
            )}
          </>
        )}
        emptyMessage="No units found for this product."
      />

      {/* Create/Edit Unit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit Unit' : 'Create New Unit'}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? `Update details for unit "${editing.instance_number}"`
                : `Create a new unit of "${template.name}"`
              }
            </DialogDescription>
          </DialogHeader>
          <AssetInstanceForm
            templateId={templateId}
            template={template}
            instance={editing}
            nextUnitSequence={stats.next_unit_sequence}
            onSuccess={handleFormSuccess}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Add Units</DialogTitle>
            <DialogDescription>
              Create multiple units of "{template.name}" at once
            </DialogDescription>
          </DialogHeader>
          <BulkInstanceCreator
            templateId={templateId}
            template={template}
            nextUnitSequence={stats.next_unit_sequence}
            onSuccess={handleBulkSuccess}
            onCancel={() => setBulkDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <CreateReservationModal
        open={reserveOpen}
        onOpenChange={setReserveOpen}
        instanceIds={reserveIds}
      />


      <PrintLabelsDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        templateId={templateId}
        productName={template.name}
        templateCode={template.template_code}
        selectedInstances={selectedInstances}
        initialMode={printDialogMode}
      />
    </div>
  );
};

export default AssetInstancesTab;
